// src/App.jsx
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useEffect, useMemo, useState, Suspense, lazy as reactLazy } from 'react';
import { AnimatePresence, MotionConfig, motion as Motion } from 'framer-motion';
// Import from the pure token module, not ./lib/anims — anims.js imports
// gsap at the top, which would drag GSAP into the App shell bundle.
import { DUR } from './lib/anim-tokens';
// TAS-358: React.lazy that reloads the page once when a route chunk fails to
// load (stale deployment, chunk served as HTML) instead of surfacing a broken
// screen the player did not navigate to.
import { lazyWithRetry as lazy } from './utils/lazyRetry';
// Perf: Route-level lazy loading to reduce initial bundle size
const IntroJoin = lazy(() => import('./pages/IntroJoin'));
const Lobby = lazy(() => import('./pages/Lobby'));
const AvatarSelect = lazy(() => import('./pages/AvatarSelect'));
const WaitingRoom = lazy(() => import('./pages/WaitingRoom'));
const Game = lazy(() => import('./pages/Game'));
const Results = lazy(() => import('./pages/Results'));
const Scoreboard = lazy(() => import('./pages/Scoreboard'));
const FinalLeaderboard = lazy(() => import('./pages/FinalLeaderboard'));
const WineReview = lazy(() => import('./pages/WineReview'));
const WineRatingReveal = lazy(() => import('./pages/WineRatingReveal'));
// NOTE: WineRevealNew is the live wine-reveal page (the old WineReveal.jsx was
// deleted in TAS-174 after a fix landed on the wrong file because of an alias).
const WineRevealPage = lazy(() => import('./pages/WineRevealNew'));
const IntroScreenPage = lazy(() => import('./pages/IntroScreen'));
const TutorialFlow = lazy(() => import('./pages/TutorialFlow'));
const RevealAnswersPage = lazy(() => import('./pages/RevealAnswers'));
const DiscussionScreenPage = lazy(() => import('./pages/DiscussionScreen'));
const GroupReviewPage = lazy(() => import('./pages/GroupReview'));
const ThankYouTestingPage = lazy(() => import('./pages/ThankYouTesting'));
const PrizePage = lazy(() => import('./pages/PrizePage'));
const PrizeRedeem = lazy(() => import('./pages/PrizeRedeem'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const WelcomePage = lazy(() => import('./pages/WelcomePage'));
const ClaimScorePage = lazy(() => import('./pages/ClaimScorePage'));
const ParticipationModePage = lazy(() => import('./pages/ParticipationMode'));
const GameViewModePage = lazy(() => import('./pages/GameViewMode'));
const TvDisplayPage = lazy(() => import('./pages/TvDisplay'));
const ConnectionCheckPage = lazy(() => import('./pages/ConnectionCheck'));
const InvitePage = lazy(() => import('./pages/InvitePage'));
const DevPreviewLastCall = import.meta.env.DEV
  ? lazy(() => import('./pages/DevPreviewLastCall'))
  : null;
const DevPreviewReveal = import.meta.env.DEV
  ? lazy(() => import('./pages/DevPreviewReveal'))
  : null;
const DevPreviewRound = import.meta.env.DEV
  ? lazy(() => import('./pages/DevPreviewRound'))
  : null;
const DevPreviewTvScoreboard = import.meta.env.DEV
  ? lazy(() => import('./pages/DevPreviewTvScoreboard'))
  : null;
const DevPreviewTvReveal = import.meta.env.DEV
  ? lazy(() => import('./pages/DevPreviewTvReveal'))
  : null;
const DevPreviewBottle = import.meta.env.DEV
  ? lazy(() => import('./pages/DevPreviewBottle'))
  : null;
const DevPreviewFinal = import.meta.env.DEV
  ? lazy(() => import('./pages/DevPreviewFinal'))
  : null;
const DevPreviewGrid = import.meta.env.DEV
  ? lazy(() => import('./pages/DevPreviewGrid'))
  : null;
const DevPreviewAnswerReveal = import.meta.env.DEV
  ? lazy(() => import('./pages/DevPreviewAnswerReveal'))
  : null;
const DevPreviewIcons = import.meta.env.DEV
  ? lazy(() => import('./pages/DevPreviewIcons'))
  : null;
const DevPreviewLesson = import.meta.env.DEV
  ? lazy(() => import('./pages/DevPreviewLesson'))
  : null;
const DevPreviewHint = import.meta.env.DEV
  ? lazy(() => import('./pages/DevPreviewHint'))
  : null;
const DevPreviewGmPrompt = import.meta.env.DEV
  ? lazy(() => import('./pages/DevPreviewGmPrompt'))
  : null;
const DevPreviewBlackout = import.meta.env.DEV
  ? lazy(() => import('./pages/DevPreviewBlackout'))
  : null;
const DevPreviewSessionHelp = import.meta.env.DEV
  ? lazy(() => import('./pages/DevPreviewSessionHelp'))
  : null;

// Eagerly loaded components (needed immediately)
//
// NotFound is deliberately NOT lazy. It is the screen that has to render when
// something else has already gone wrong — a link that predates this build, a
// chunk that will not fetch — so it must not itself depend on fetching a chunk.
import NotFound from './pages/NotFound';
import FullscreenLayout from './components/FullscreenLayout';
import GameWrapper from './pages/GameWrapper';
import TvPhoneGate from './components/shared/TvPhoneGate';
import WineRevealGate from './components/shared/WineRevealGate';
import { GameFlowProvider } from './contexts/GameFlowProvider';
import { usePageTracking } from '@tasted/utils/analytics-hooks';
import { debugPanelEnabled } from '@tasted/utils/analytics-debug-gate';
// Perf: the analytics debug panel is dev-only and off by default, so its chunk
// is only requested once ?debug=analytics has switched it on. Plain React.lazy
// rather than lazyWithRetry: a stale debug chunk is not worth reloading over.
const AnalyticsDebugPanel = import.meta.env.DEV
  ? reactLazy(() => import('@tasted/utils/analytics-debug'))
  : null;
import LoadingScreen from './components/shared/LoadingScreen';
import { ConnectivityBanner } from './components/shared/ConnectivityIndicator';
import { Toaster } from 'sonner';
import { SentryErrorBoundary } from './components/ErrorBoundary';
import ScreenErrorBoundary from './components/ScreenErrorBoundary';
import { SessionHelpProvider } from './components/session-help/SessionHelpProvider';
// Tiny module — NOT emoteSystem.jsx, which would drag gsap + all emote WebPs
// into the app shell (perf audit 2026-07-14).
import { dismissActiveEmotes } from './lib/emoteDismiss';

export default function App() {
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [publicLoading, setPublicLoading] = useState(false);

  // Public loader event bus for cross-fades on public pages
  useEffect(() => {
    function onShow() {
      setPublicLoading(true);
    }
    function onHide() {
      setPublicLoading(false);
    }
    window.addEventListener('public-loader-show', onShow);
    window.addEventListener('public-loader-hide', onHide);
    return () => {
      window.removeEventListener('public-loader-show', onShow);
      window.removeEventListener('public-loader-hide', onHide);
    };
  }, []);

  // Keep LoadingScreen visible until GameFlowProvider signals session data + step-0 assets are ready.
  // Falls back to hiding after 5s so the screen can never get permanently stuck.
  useEffect(() => {
    if (!location.pathname.startsWith('/game')) {
      setLoading(false);
      return;
    }
    const safetyTimer = setTimeout(() => setLoading(false), 5000);
    function onReady() {
      clearTimeout(safetyTimer);
      setLoading(false);
    }
    window.addEventListener('game-session-ready', onReady);
    return () => {
      clearTimeout(safetyTimer);
      window.removeEventListener('game-session-ready', onReady);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Use enhanced page tracking with deduplication
  usePageTracking({
    enabled: true,
    additionalProps: {
      app: 'game',
      version: '1.0.0',
    },
  });

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const sessionCode = params.get('code');
  // Re-evaluated per navigation so ?debug=analytics / ?debug=off take effect
  // without a reload (the gate itself persists the choice in localStorage).
  const showDebugPanel = useMemo(
    () => Boolean(AnalyticsDebugPanel) && debugPanelEnabled(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.search]
  );

  // Any route change fades out playing celebration emotes — they're
  // fixed-position overlays, and public screens (e.g. AvatarSelect's
  // pick-an-avatar emotes) aren't covered by GameWrapper's in-game dismiss,
  // so one could keep playing over the next screen mid-boomerang.
  useEffect(() => {
    dismissActiveEmotes();
  }, [location.pathname, location.search]);

  // Safety: the public loader must never get stuck after navigating to a public
  // route. A soft hide once the destination has had a moment to mount, plus a
  // hard backstop. (LoadingScreen also self-heals via its own max-visible
  // watchdog, so this is defence-in-depth, not the sole guard.)
  useEffect(() => {
    if (location.pathname.startsWith('/game')) return undefined;
    const softId = setTimeout(() => setPublicLoading(false), 2000);
    const hardId = setTimeout(() => setPublicLoading(false), 8000);
    return () => {
      clearTimeout(softId);
      clearTimeout(hardId);
    };
  }, [location.pathname, location.search]);

  // Perf: Suspense fallback for lazy-loaded routes (LoadingScreen handles the actual loading UI)
  //
  // `location` is PINNED on <Routes>. The public branch below wraps this in
  // AnimatePresence mode="wait", which keeps rendering the OUTGOING Motion.div
  // for the 300ms exit fade — and an unpinned <Routes> inside it reads the NEW
  // location from context, so the next screen was painted twice: a throwaway
  // copy inside the dying wrapper for 300ms, then the real mount. Any tap on
  // the copy (an avatar tile, a lobby button) landed on a component about to
  // be unmounted and was lost — the "tap does nothing right after a screen
  // change" flake (E2E 2026-08-16, TAS-257). Pinned, the exit copy keeps
  // showing the old screen, as the fade intends.
  const routes = (
    <Suspense fallback={null}>
      <Routes location={location}>
        {/* Public screens */}
        {DevPreviewLastCall && (
          <Route path="/dev/last-call" element={<DevPreviewLastCall />} />
        )}
        {DevPreviewReveal && (
          <Route path="/dev/reveal" element={<DevPreviewReveal />} />
        )}
        {DevPreviewTvScoreboard && (
          <Route path="/dev/tv-scoreboard" element={<DevPreviewTvScoreboard />} />
        )}
        {DevPreviewTvReveal && (
          <Route path="/dev/tv-reveal" element={<DevPreviewTvReveal />} />
        )}
        {DevPreviewBottle && (
          <Route path="/dev/bottle" element={<DevPreviewBottle />} />
        )}
        {DevPreviewRound && (
          <Route path="/dev/round" element={<DevPreviewRound />} />
        )}
        {DevPreviewGrid && (
          <Route path="/dev/grid" element={<DevPreviewGrid />} />
        )}
        {DevPreviewAnswerReveal && (
          <Route path="/dev/answer-reveal" element={<DevPreviewAnswerReveal />} />
        )}
        {DevPreviewIcons && <Route path="/dev/icons" element={<DevPreviewIcons />} />}
        {DevPreviewFinal && (
          <Route path="/dev/final" element={<DevPreviewFinal />} />
        )}
        {DevPreviewGmPrompt && (
          <Route path="/dev/gm-prompt" element={<DevPreviewGmPrompt />} />
        )}
        {DevPreviewBlackout && (
          <Route path="/dev/blackout" element={<DevPreviewBlackout />} />
        )}
        {DevPreviewLesson && <Route path="/dev/lesson" element={<DevPreviewLesson />} />}
        {DevPreviewSessionHelp && (
          <Route path="/dev/session-help" element={<DevPreviewSessionHelp />} />
        )}
        {DevPreviewHint && (
          <Route path="/dev/hint" element={<DevPreviewHint />} />
        )}
        <Route path="/prize/redeem" element={<PrizeRedeem />} />
        <Route path="/welcome" element={<WelcomePage />} />
        {/* Where the recap email's "Claim score" button lands. Public and
            capability-free: it carries the session code and nothing else. */}
        <Route path="/claim" element={<ClaimScorePage />} />
        {/* Host invite: moved to tasted.co.za/invite/:code; this forwards old links (see InvitePage). */}
        <Route path="/invite/:code" element={<InvitePage />} />
        <Route path="/"         element={<IntroJoin />} />
        <Route path="/join/:code" element={<IntroJoin />} />
        <Route
          path="/onboarding"
          element={
            <FullscreenLayout>
              <TutorialFlow />
            </FullscreenLayout>
          }
        />
        <Route
          path="/lobby"
          element={
            <FullscreenLayout>
              <Lobby />
            </FullscreenLayout>
          }
        />
        <Route
          path="/avatar"
          element={
            <FullscreenLayout>
              <AvatarSelect />
            </FullscreenLayout>
          }
        />
        <Route
          path="/waitingroom"
          element={
            <FullscreenLayout>
              <WaitingRoom />
            </FullscreenLayout>
          }
        />

        {/* Game routes – All nested under /game */}
        <Route
          path="/game"
          element={sessionCode ? <GameWrapper /> : <Navigate to="/" replace />}
        >
          <Route index element={<Game />} /> {/* Matches /game?code=... */}
          <Route path="intro" element={<IntroScreenPage />} />
          <Route path="reveal" element={<RevealAnswersPage />} />
          <Route path="discussion" element={<DiscussionScreenPage />} />
          <Route path="results" element={<Results />} />
          {/* TV games: the phone shows "how you scored"; Scoreboard handles it (isTvPlayer). */}
          <Route path="scoreboard" element={<Scoreboard />} />
          <Route path="final-leaderboard" element={<TvPhoneGate subtitle="The final results are on the big screen"><FinalLeaderboard /></TvPhoneGate>} />
          <Route path="review" element={<WineReview />} />
          <Route path="wine-rating-reveal" element={<TvPhoneGate subtitle="The group's rating is on the big screen"><WineRatingReveal /></TvPhoneGate>} />
          <Route path="wine-reveal" element={<WineRevealGate><WineRevealPage /></WineRevealGate>} />
          <Route path="group-review" element={<GroupReviewPage />} />
          <Route path="prize" element={<PrizePage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="thank-you" element={<ThankYouTestingPage />} />
          <Route path="participation-mode" element={<ParticipationModePage />} />
          <Route path="game-view-mode" element={<GameViewModePage />} />
        </Route>

        {/* TAS-274: standalone TV big-screen display. Passive listener — needs
            only ?code= (or /tv/:code), no player row. Wrapped in a display-mode
            GameFlowProvider below. */}
        <Route path="/tv" element={<TvDisplayPage />} />
        <Route path="/tv/:code" element={<TvDisplayPage />} />
        {/* game.tasted.co.za/check — what this phone's device and connection
            gates decided, for when a table says the 3D bottle didn't show. */}
        <Route path="/check" element={<ConnectionCheckPage />} />

        {/* Anything else. Without this, an unrecognised path rendered the shell
            and nothing inside it, which is indistinguishable from a broken app.
            It catches unknown children of /game too: no child route matches, so
            the match falls through to here rather than to the /game parent. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );

  return (
    // reducedMotion="user" makes EVERY framer-motion component in the game honour
    // prefers-reduced-motion (transforms/layout animations are dropped, opacity
    // fades kept) — one switch instead of guarding each screen. No effect for
    // users who haven't requested reduced motion.
    <MotionConfig reducedMotion="user">
    <SentryErrorBoundary>
      <Toaster richColors position="top-center" />
      {/* Lost-connection banner (navigator.onLine + shared realtime socket),
          on every route. The always-on signal icon beside it was removed
          (Mel, 2026-10-01): it only knew online / reconnecting / offline, not
          connection quality, so when healthy it said nothing. The banner still
          says so, loudly, when the connection actually drops. */}
      <ConnectivityBanner />
      {/* Analytics Debug Panel - only in development, behind ?debug=analytics */}
      {AnalyticsDebugPanel && showDebugPanel && (
        <Suspense fallback={null}>
          <AnalyticsDebugPanel position="bottom-right" />
        </Suspense>
      )}
      {/* Global loaders (render first so overlay can paint before route content) */}
      {location.pathname.startsWith('/game') ? (
        <LoadingScreen loading={loading} message={loadingMessage} />
      ) : (
        <LoadingScreen loading={publicLoading} />
      )}
      {/* Part 2 (M4): Help & session sits ABOVE every route branch, so on /game it
          wraps GameFlowProvider and stays outside ScreenErrorBoundary — a crash
          loop on a screen still leaves Support and a GM handover reachable. It
          reads its own state; GameFlowProvider registers into it when present. */}
      <SessionHelpProvider>
      {location.pathname.startsWith('/game') ? (
        // TAS-26: the boundary lives INSIDE the provider so one screen's crash
        // can't take the room's connection down with it (see ScreenErrorBoundary).
        <GameFlowProvider><ScreenErrorBoundary>{routes}</ScreenErrorBoundary></GameFlowProvider>
      ) : location.pathname.startsWith('/tv') ? (
        // TAS-274: the TV display is a player-less follower — mount the provider
        // in displayMode so it mirrors the room without ever driving it.
        <GameFlowProvider displayMode><ScreenErrorBoundary>{routes}</ScreenErrorBoundary></GameFlowProvider>
      ) : (
        <div className="relative min-h-[100dvh]">
          {/* mode="wait": fully unmount the exiting route before mounting the
              next one, so two copies of a screen are never in the DOM at once
              (avoids doubled interactive elements / duplicated screen-reader
              content during the fade). */}
          <AnimatePresence mode="wait" initial={false}>
            <Motion.div
              key={
                location.pathname +
                location.search +
                (location.state?.skipPublicFade ? '-nofade' : '')
              }
              initial={
                location.state?.skipPublicFade ? { opacity: 1 } : { opacity: 0 }
              }
              animate={{ opacity: 1 }}
              exit={
                location.state?.skipPublicFade ? { opacity: 1 } : { opacity: 0 }
              }
              transition={{ duration: DUR.med, ease: 'easeInOut' }}
              className="absolute inset-0"
            >
              <ScreenErrorBoundary>{routes}</ScreenErrorBoundary>
            </Motion.div>
          </AnimatePresence>
        </div>
      )}
      </SessionHelpProvider>
    </SentryErrorBoundary>
    </MotionConfig>
  );
}
